/**
 * @author      david.loehr@zumper.com (David Loehr)
 * @copyright   Copyright (c) 2023, Zumper
 */

import { Zumper } from '@zcode-ui/icons'
import { useCallback, useEffect, useState } from 'react'
import { twMerge } from 'tailwind-merge'
import './ZumperLoader.css'

export interface ZumperLoaderProps {
  'data-testid'?: string
  /** Duration of the second, pulse animation in seconds */
  pulseSpeed?: number
  size?: number
  /** Duration of the first, "fill" animation in seconds */
  speed?: number
  containerClassName?: string
}

/**
 * Renders Zumper Logo with animation
 */
export const ZumperLoader = ({
  containerClassName,
  pulseSpeed = 2,
  size,
  speed = 10,
  ...rest
}: ZumperLoaderProps) => {
  const [animation, setAnimation] = useState<'addColor' | 'pulse'>('addColor')
  const [animatedElement, setAnimatedElement] = useState<HTMLDivElement | null>(
    null
  )
  const onAnimationEnd = useCallback(() => {
    setAnimation('pulse')
  }, [])
  useEffect(() => {
    if (animatedElement) {
      animatedElement.addEventListener('animationend', onAnimationEnd, {
        once: true,
      })
      return () => {
        animatedElement?.removeEventListener('animationend', onAnimationEnd)
      }
    }
  }, [onAnimationEnd, animatedElement])
  return (
    <div
      aria-label="Loading..."
      className={twMerge('relative pointer-events-none', containerClassName)}
      role="img"
      {...rest}
    >
      <div className="grayscale opacity-50">
        <Zumper size={size} />
      </div>
      <div
        className="absolute top-0"
        style={{
          animation:
            animation === 'addColor'
              ? `add-color ${speed}s linear`
              : `pulse ${pulseSpeed}s ease infinite`,
        }}
        ref={setAnimatedElement}
      >
        <Zumper size={size} />
      </div>
    </div>
  )
}
